import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  ArrowLeft, 
  ArrowRight, 
  Send, 
  Check,
  LogOut,
  User,
  RefreshCw,
  ChevronRight
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import CurrencySelector from '@/components/CurrencySelector';
import FeeBreakdown from '@/components/FeeBreakdown';
import { getCurrency, convertCurrency } from '@/utils/currencies';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ModernTransferPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [recipients, setRecipients] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [transferData, setTransferData] = useState({
    amount: '',
    fromCurrency: 'USD',
    toCurrency: 'MXN',
    paymentMethod: 'bankTransfer',
    recipientId: '',
    recipientName: '',
    recipientAccount: '',
    useExistingRecipient: true
  });

  const [errors, setErrors] = useState({
    amount: '',
    recipientName: '',
    recipientAccount: ''
  });

  useEffect(() => {
    loadRecipients();
  }, []);

  const loadRecipients = async () => {
    try{
      const response = await axios.get(`${API}/recipients?user_id=${user.id}`);
      setRecipients(response.data);
    } catch (error) {
      console.error('Failed to load recipients:', error);
      toast.error('Failed to load saved recipients');
    } finally {
      setLoadingRecipients(false);
    }
  };

  // Validation functions
  const validateAmount = (amount) => {
    if (!amount || amount === '') {
      return 'Amount is required';
    }
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) {
      return 'Please enter a valid number';
    }
    if (numAmount <= 0) {
      return 'Amount must be greater than 0';
    }
    if (numAmount < 1) {
      return 'Minimum transfer amount is $1';
    }
    if (numAmount > 50000) {
      return 'Maximum transfer amount is $50,000';
    }
    return '';
  };

  const validateRecipientName = (name) => {
    if (!name || name.trim() === '') {
      return 'Recipient name is required';
    }
    if (name.trim().length < 2) {
      return 'Name must be at least 2 characters';
    }
    if (name.length > 100) {
      return 'Name must be less than 100 characters';
    }
    // Basic sanitization check
    if (/<|>|script/i.test(name)) {
      return 'Invalid characters in name';
    }
    return '';
  };

  const validateRecipientAccount = (account) => {
    if (!account || account.trim() === '') {
      return 'Account number is required';
    }
    if (account.trim().length < 5) {
      return 'Account number must be at least 5 characters';
    }
    if (account.length > 100) {
      return 'Account number must be less than 100 characters';
    }
    return '';
  };

  const handleAmountChange = (value) => {
    setTransferData({ ...transferData, amount: value });
    const error = validateAmount(value);
    setErrors({ ...errors, amount: error });
  };

  const handleRecipientNameChange = (value) => {
    setTransferData({ ...transferData, recipientName: value });
    const error = validateRecipientName(value);
    setErrors({ ...errors, recipientName: error });
  };

  const handleRecipientAccountChange = (value) => {
    setTransferData({ ...transferData, recipientAccount: value });
    const error = validateRecipientAccount(value);
    setErrors({ ...errors, recipientAccount: error });
  };

  const selectedRecipient = recipients.find(r => r.id === transferData.recipientId);
  const fromCurrency = getCurrency(transferData.fromCurrency);
  const toCurrency = getCurrency(transferData.toCurrency);

  const canProceedStep1 = transferData.amount && 
                         parseFloat(transferData.amount) > 0 && 
                         !errors.amount;
  const canProceedStep2 = transferData.useExistingRecipient 
    ? transferData.recipientId 
    : (transferData.recipientName && 
       transferData.recipientAccount && 
       !errors.recipientName && 
       !errors.recipientAccount);

  const handleSubmit = async () => {
    // Final validation before submission
    const amountError = validateAmount(transferData.amount);
    if (amountError) {
      toast.error(amountError);
      setStep(1);
      return;
    }

    if (!transferData.useExistingRecipient) {
      const nameError = validateRecipientName(transferData.recipientName);
      const accountError = validateRecipientAccount(transferData.recipientAccount);
      
      if (nameError || accountError) {
        toast.error('Please fix recipient information errors');
        setStep(2);
        return;
      }
    }

    setSubmitting(true);
    try {
      const transactionData = {
        amount: parseFloat(transferData.amount),
        from_currency: transferData.fromCurrency,
        to_currency: transferData.toCurrency,
        from_country: fromCurrency?.region || 'Unknown',
        to_country: toCurrency?.region || 'Unknown',
        recipient_name: transferData.useExistingRecipient 
          ? selectedRecipient?.name 
          : transferData.recipientName,
        recipient_account: transferData.useExistingRecipient 
          ? selectedRecipient?.account_number 
          : transferData.recipientAccount
      };

      const response = await axios.post(`${API}/transactions`, transactionData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      toast.success('Transfer initiated successfully!');
      
      // Small delay to show success message
      setTimeout(() => {
        navigate('/dashboard');
      }, 1000);
      
    } catch (error) {
      console.error('Transfer failed:', error);
      
      // Handle different error types
      if (error.response) {
        const status = error.response.status;
        const message = error.response.data?.detail || error.response.data?.message;
        
        if (status === 401) {
          toast.error('Session expired. Please login again');
          setTimeout(() => navigate('/'), 2000);
        } else if (status === 400) {
          toast.error(message || 'Invalid transfer data');
        } else if (status === 429) {
          toast.error('Too many requests. Please try again later');
        } else if (status >= 500) {
          toast.error('Server error. Please try again later');
        } else {
          toast.error(message || 'Failed to initiate transfer');
        }
      } else if (error.request) {
        toast.error('Network error. Please check your connection');
      } else {
        toast.error('An unexpected error occurred');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const recipientAmount = transferData.amount && fromCurrency && toCurrency
    ? convertCurrency(parseFloat(transferData.amount), transferData.fromCurrency, transferData.toCurrency)
    : '0.00';

  return (
    <div className='min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50'>
      {/* Navigation */}
      <nav className='bg-white border-b shadow-sm'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex justify-between items-center h-16'>
            <div className='flex items-center space-x-3 cursor-pointer' onClick={() => navigate('/dashboard')}>
              <img 
                src='https://customer-assets.emergentagent.com/job_smart-money-route/artifacts/6teoyzj7_Generated%20Image%20November%2018%2C%202025%20-%203_06PM.png' 
                alt='RouteFlow AI' 
                className='h-10 w-auto'
              />
              <span className='text-xl font-bold gradient-text hidden sm:inline'>RouteFlow AI</span>
            </div>
            <div className='flex items-center space-x-4'>
              <Button variant='ghost' className='flex items-center space-x-2' onClick={() => navigate('/dashboard')}>
                <User className='w-4 h-4' />
                <span className='hidden sm:inline'>{user?.email}</span>
              </Button>
              <Button variant='ghost' onClick={onLogout}>
                <LogOut className='w-4 h-4' />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className='max-w-6xl mx-auto px-4 py-8'>
        {/* Progress Steps */}
        <div className='mb-8'>
          <div className='flex items-center justify-center space-x-4'>
            {[1, 2, 3].map((s) => (
              <div key={s} className='flex items-center'>
                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all ${
                  step >= s 
                    ? 'bg-emerald-500 border-emerald-500 text-white' 
                    : 'bg-white border-gray-300 text-gray-400'
                }`}>
                  {step > s ? <Check className='w-5 h-5' /> : s}
                </div>
                {s < 3 && (
                  <div className={`w-16 h-1 transition-all ${step > s ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                )}
              </div>
            ))}
          </div>
          <div className='flex justify-between mt-3 text-sm text-gray-600 max-w-md mx-auto'>
            <span className={step === 1 ? 'font-semibold text-emerald-600' : ''}>Amount & Currency</span>
            <span className={step === 2 ? 'font-semibold text-emerald-600' : ''}>Recipient</span>
            <span className={step === 3 ? 'font-semibold text-emerald-600' : ''}>Confirm</span>
          </div>
        </div>

        {/* Step 1: Amount & Currency Selection */}
        {step === 1 && (
          <div className='grid md:grid-cols-2 gap-6'>
            {/* Left: Transfer Details */}
            <div className='space-y-6'>
              <Card className='border-2 border-emerald-500 shadow-xl'>
                <CardHeader>
                  <CardTitle className='text-xl'>Send Money Globally</CardTitle>
                  <CardDescription>Choose currencies and enter amount</CardDescription>
                </CardHeader>
                <CardContent className='space-y-6'>
                  {/* From Currency */}
                  <CurrencySelector
                    value={transferData.fromCurrency}
                    onChange={(code) => setTransferData({ ...transferData, fromCurrency: code })}
                    label='You Send'
                    type='from'
                    excludeCurrency={transferData.toCurrency}
                    showPopularCorridors={false}
                  />

                  {/* Amount Input */}
                  <div className='space-y-2'>
                    <Label htmlFor='amount'>Amount</Label>
                    <div className='relative'>
                      <span className='absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-semibold text-gray-600'>
                        {fromCurrency?.symbol || '$'}
                      </span>
                      <Input
                        id='amount'
                        type='number'
                        step='0.01'
                        min='1'
                        max='50000'
                        placeholder='0.00'
                        value={transferData.amount}
                        onChange={(e) => handleAmountChange(e.target.value)}
                        className={`pl-12 pr-4 text-3xl h-20 font-bold border-2 ${errors.amount ? 'border-red-500' : ''}`}
                      />
                    </div>
                    {errors.amount && (
                      <p className='text-sm text-red-600 mt-1'>{errors.amount}</p>
                    )}
                    <p className='text-xs text-gray-500'>Minimum: $1 | Maximum: $50,000</p>
                  </div>

                  {/* Currency Swap Button */}
                  <div className='flex justify-center'>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => setTransferData({
                        ...transferData,
                        fromCurrency: transferData.toCurrency,
                        toCurrency: transferData.fromCurrency
                      })}
                      className='rounded-full border-2'
                    >
                      <RefreshCw className='w-4 h-4 mr-2' />
                      Swap Currencies
                    </Button>
                  </div>

                  {/* To Currency */}
                  <CurrencySelector
                    value={transferData.toCurrency}
                    onChange={(code) => setTransferData({ ...transferData, toCurrency: code })}
                    label='Recipient Gets'
                    type='to'
                    excludeCurrency={transferData.fromCurrency}
                    showPopularCorridors={true}
                    otherCurrency={transferData.fromCurrency}
                  />

                  {/* Recipient Amount Display */}
                  {transferData.amount && parseFloat(transferData.amount) > 0 && (
                    <div className='bg-emerald-50 border-2 border-emerald-200 rounded-lg p-4'>
                      <p className='text-sm text-gray-600 mb-1'>Recipient receives</p>
                      <p className='text-3xl font-bold text-emerald-600'>
                        {toCurrency?.symbol}{recipientAmount} {transferData.toCurrency}
                      </p>
                      <p className='text-xs text-gray-500 mt-2'>
                        Exchange rate: 1 {transferData.fromCurrency} = {(toCurrency?.rate / fromCurrency?.rate).toFixed(4)} {transferData.toCurrency}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Right: Fee Breakdown */}
            <div>
              <FeeBreakdown
                amount={transferData.amount}
                fromCurrency={transferData.fromCurrency}
                toCurrency={transferData.toCurrency}
                paymentMethod={transferData.paymentMethod}
                onPaymentMethodChange={(method) => setTransferData({ ...transferData, paymentMethod: method })}
              />
            </div>
          </div>
        )}

        {/* Step 2: Recipient Selection */}
        {step === 2 && (
          <Card className='border-0 shadow-xl max-w-2xl mx-auto'>
            <CardHeader>
              <CardTitle>Choose Recipient</CardTitle>
              <CardDescription>Select existing or add new recipient</CardDescription>
            </CardHeader>
            <CardContent className='space-y-6'>
              {/* Toggle: Existing vs New */}
              <div className='flex space-x-2 p-1 bg-gray-100 rounded-lg'>
                <button
                  onClick={() => setTransferData({ ...transferData, useExistingRecipient: true })}
                  className={`flex-1 py-2 px-4 rounded-md font-medium transition-all ${
                    transferData.useExistingRecipient
                      ? 'bg-white shadow-sm text-emerald-600'
                      : 'text-gray-600'
                  }`}
                >
                  Existing Recipient
                </button>
                <button
                  onClick={() => setTransferData({ ...transferData, useExistingRecipient: false })}
                  className={`flex-1 py-2 px-4 rounded-md font-medium transition-all ${
                    !transferData.useExistingRecipient
                      ? 'bg-white shadow-sm text-emerald-600'
                      : 'text-gray-600'
                  }`}
                >
                  New Recipient
                </button>
              </div>

              {/* Existing Recipients */}
              {transferData.useExistingRecipient && (
                <div className='space-y-3'>
                  {loadingRecipients ? (
                    <p className='text-center text-gray-500 py-8'>Loading recipients...</p>
                  ) : recipients.length > 0 ? (
                    recipients.map((recipient) => (
                      <button
                        key={recipient.id}
                        onClick={() => setTransferData({ ...transferData, recipientId: recipient.id })}
                        className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
                          transferData.recipientId === recipient.id
                            ? 'border-emerald-500 bg-emerald-50'
                            : 'border-gray-200 hover:border-emerald-300'
                        }`}
                      >
                        <div className='flex items-center justify-between'>
                          <div>
                            <p className='font-semibold text-gray-900'>{recipient.name}</p>
                            <p className='text-sm text-gray-600'>
                              {recipient.bank_name} • {recipient.account_number}
                            </p>
                            <p className='text-xs text-gray-500 mt-1'>{recipient.country}</p>
                          </div>
                          {transferData.recipientId === recipient.id && (
                            <div className='w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center'>
                              <Check className='w-4 h-4 text-white' />
                            </div>
                          )}
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className='text-center py-8'>
                      <p className='text-gray-600 mb-4'>No saved recipients yet</p>
                      <Button
                        variant='outline'
                        onClick={() => setTransferData({ ...transferData, useExistingRecipient: false })}
                      >
                        Add New Recipient
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* New Recipient Form */}
              {!transferData.useExistingRecipient && (
                <div className='space-y-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='recipientName'>Recipient Name</Label>
                    <Input
                      id='recipientName'
                      placeholder='Enter full name'
                      value={transferData.recipientName}
                      onChange={(e) => handleRecipientNameChange(e.target.value)}
                      className={errors.recipientName ? 'border-red-500' : ''}
                    />
                    {errors.recipientName && (
                      <p className='text-sm text-red-600'>{errors.recipientName}</p>
                    )}
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='recipientAccount'>Account Number / Wallet Address</Label>
                    <Input
                      id='recipientAccount'
                      placeholder='Enter account details'
                      value={transferData.recipientAccount}
                      onChange={(e) => handleRecipientAccountChange(e.target.value)}
                      className={errors.recipientAccount ? 'border-red-500' : ''}
                    />
                    {errors.recipientAccount && (
                      <p className='text-sm text-red-600'>{errors.recipientAccount}</p>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 3: Confirmation */}
        {step === 3 && (
          <div className='max-w-2xl mx-auto space-y-6'>
            <Card className='border-2 border-emerald-500 shadow-xl'>
              <CardHeader>
                <CardTitle>Confirm Your Transfer</CardTitle>
                <CardDescription>Please review all details before submitting</CardDescription>
              </CardHeader>
              <CardContent className='space-y-4'>
                {/* Transfer Summary */}
                <div className='bg-gray-50 rounded-lg p-4 space-y-3'>
                  <div className='flex justify-between'>
                    <span className='text-gray-600'>You Send</span>
                    <span className='font-semibold'>
                      {fromCurrency?.symbol}{parseFloat(transferData.amount).toFixed(2)} {transferData.fromCurrency}
                    </span>
                  </div>
                  <div className='flex justify-between'>
                    <span className='text-gray-600'>Recipient Gets</span>
                    <span className='font-semibold'>
                      {toCurrency?.symbol}{recipientAmount} {transferData.toCurrency}
                    </span>
                  </div>
                  <div className='flex justify-between'>
                    <span className='text-gray-600'>Recipient</span>
                    <span className='font-semibold'>
                      {transferData.useExistingRecipient ? selectedRecipient?.name : transferData.recipientName}
                    </span>
                  </div>
                  <div className='flex justify-between'>
                    <span className='text-gray-600'>Payment Method</span>
                    <span className='font-semibold capitalize'>
                      {transferData.paymentMethod === 'bankTransfer' ? 'Bank Transfer' : 
                       transferData.paymentMethod === 'card' ? 'Card Payment' : 'PayPal'}
                    </span>
                  </div>
                </div>

                {/* Important Notice */}
                <div className='bg-amber-50 border border-amber-200 rounded-lg p-4'>
                  <p className='text-sm text-amber-900 font-semibold mb-1'>Important</p>
                  <p className='text-xs text-amber-800'>
                    This feature is being finalized. Your transfer will be processed once our payment provider integration is complete.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className='flex justify-between items-center mt-8 max-w-2xl mx-auto'>
          {step > 1 ? (
            <Button
              variant='outline'
              onClick={() => setStep(step - 1)}
              className='flex items-center space-x-2'
            >
              <ArrowLeft className='w-4 h-4' />
              <span>Back</span>
            </Button>
          ) : (
            <Button
              variant='ghost'
              onClick={() => navigate('/dashboard')}
            >
              Cancel
            </Button>
          )}

          {step < 3 ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={step === 1 ? !canProceedStep1 : !canProceedStep2}
              className='flex items-center space-x-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700'
            >
              <span>Continue</span>
              <ArrowRight className='w-4 h-4' />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className='flex items-center space-x-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700'
            >
              {submitting ? (
                <>
                  <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin' />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <Send className='w-4 h-4' />
                  <span>Confirm Transfer</span>
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModernTransferPage;
